import React, { ReactNode } from 'react';
import Link from 'next/link';

import cx from 'classnames';

import Spinner from 'components/Spinner';

import { Arrow } from '../Arrow/Arrow';

import s from './Button.module.css';

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  ariaHidden?: boolean;
  arrowClassName?: string;
  className?: string;
  children: ReactNode;
  loading?: boolean;
  to?: URL | string;
  newTab?: boolean;
  outline?: boolean;
  wrapArrow?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, Props>(
  (
    {
      ariaHidden,
      arrowClassName,
      className,
      children,
      loading = false,
      type = 'submit',
      to,
      disabled = false,
      outline = false,
      newTab,
      wrapArrow,
      ...other
    },
    ref,
  ) => {
    const otherProps = { ...other };

    const classNames = cx(
      s.button,
      {
        [s.outline]: outline,
      },
      className,
    );

    const renderInsideButton = () => (
      <>
        <span
          className={cx(s.wrapper, {
            [s.wrapped]: wrapArrow,
            [s.visHid]: loading,
          })}
        >
          {children}
          <span className={cx(s.arrow, arrowClassName)}>
            <Arrow />
          </span>
        </span>
        <Spinner loading={loading} />
      </>
    );

    if (to) {
      if (newTab) {
        return (
          <a
            href={to as string}
            className={classNames}
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        );
      }

      return (
        <Link
          href={to}
          className={classNames}
          aria-hidden={ariaHidden || undefined}
        >
          {renderInsideButton()}
        </Link>
      );
    }

    return (
      <button
        // eslint-disable-next-line react/button-has-type
        type={type}
        className={classNames}
        {...otherProps}
        disabled={disabled}
        ref={ref}
      >
        {renderInsideButton()}
      </button>
    );
  },
);

Button.displayName = 'Button';

export { Button };
